import React, { useState, useEffect } from 'react';
import { Calendar, CheckCircle, XCircle, RefreshCw, User, Phone, MessageSquare, FileSpreadsheet, Clock, Play } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getHistoricoEnvios, listarJobsProcessamento, obterStatusProcessamento } from '../services/api';
import api from '../services/api';
import './Historico.css';

const Historico = () => {
  const [historico, setHistorico] = useState([]);
  const [historicoEnvios, setHistoricoEnvios] = useState([]);
  const [estatisticas, setEstatisticas] = useState({
    total_execucoes: 0,
    execucoes_sucesso: 0,
    execucoes_erro: 0
  });
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todos');
  const [abaAtiva, setAbaAtiva] = useState('jobs-excel');
  const [jobsExcel, setJobsExcel] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  
  // Estados para paginação e busca de envios
  const [enviosPagination, setEnviosPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loadingEnvios, setLoadingEnvios] = useState(false);

  useEffect(() => {
    carregarHistorico();
    carregarEstatisticas();
    carregarHistoricoEnvios();
    carregarJobsExcel();
    
    // Auto-atualizar jobs em andamento a cada 10 segundos
    const interval = setInterval(() => {
      if (abaAtiva === 'jobs-excel') {
        carregarJobsExcel();
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [abaAtiva]);

  const carregarHistorico = async () => {
    try {
      const response = await api.get('/historico');
      setHistorico(response.data);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    } finally {
      setLoading(false);
    }
  };

  const carregarEstatisticas = async () => {
    try {
      const response = await api.get('/estatisticas');
      const stats = response.data;
      setEstatisticas({
        total_execucoes: stats.total_execucoes || 0,
        execucoes_sucesso: stats.execucoes_sucesso || 0,
        execucoes_erro: stats.execucoes_erro || 0
      });
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const carregarJobsExcel = async () => {
    try {
      setLoadingJobs(true);
      const response = await listarJobsProcessamento(50, 0);
      setJobsExcel(response.data.jobs || []);
    } catch (error) {
      console.error('Erro ao carregar jobs Excel:', error);
    } finally {
      setLoadingJobs(false);
    }
  };

  const carregarHistoricoEnvios = async (page = 1, search = '', status = '') => {
    try {
      setLoadingEnvios(true);
      const response = await getHistoricoEnvios(page, enviosPagination.limit, search, status);
      const data = response.data;
      
      setHistoricoEnvios(data.envios);
      setEnviosPagination({
        page: data.page,
        limit: data.limit,
        total: data.total,
        totalPages: data.total_pages
      });
    } catch (error) {
      console.error('Erro ao carregar histórico de envios:', error);
    } finally {
      setLoadingEnvios(false);
    }
  };

  const formatarData = (data) => {
    return format(new Date(data), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  };

  const handleSearch = () => {
    setEnviosPagination(prev => ({ ...prev, page: 1 }));
    carregarHistoricoEnvios(1, searchTerm, statusFilter);
  };

  const handlePageChange = (newPage) => {
    carregarHistoricoEnvios(newPage, searchTerm, statusFilter);
  };

  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    setEnviosPagination(prev => ({ ...prev, page: 1 }));
    carregarHistoricoEnvios(1, searchTerm, newStatus);
  };

  const filtrarHistorico = () => {
    if (filtro === 'sucesso') {
      return historico.filter(item => item.sucesso);
    } else if (filtro === 'erro') {
      return historico.filter(item => !item.sucesso);
    }
    return historico;
  };

  const historicoFiltrado = filtrarHistorico();

  if (loading) {
    return (
      <div className="container">
        <div className="text-center p-6">
          <div className="loading"></div>
          <p className="mt-4">Carregando histórico...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="historico-header">
        <h1 className="historico-page-title">Histórico de Processamentos</h1>
        <p className="historico-page-subtitle">
          Acompanhe todas as execuções do sistema
        </p>
      </div>

      <div className="historico-card">
        <div className="historico-filters">
          <div className="historico-filter-group">
            <label className="historico-form-label">Filtrar por status:</label>
            <select 
              value={filtro} 
              onChange={(e) => setFiltro(e.target.value)}
              className="historico-form-input"
            >
              <option value="todos">Todos</option>
              <option value="sucesso">Sucesso</option>
              <option value="erro">Com Erro</option>
            </select>
          </div>
          
          <button 
            className="historico-refresh-button"
            onClick={() => {
              carregarHistorico();
              carregarEstatisticas();
              carregarHistoricoEnvios();
              carregarJobsExcel();
            }}
          >
            <RefreshCw size={18} />
            Atualizar
          </button>
        </div>

        {/* Abas */}
        <div className="historico-tabs-container mb-6">
          <div className="historico-tabs">
            <button 
              className={`historico-tab ${abaAtiva === 'jobs-excel' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('jobs-excel')}
            >
              <FileSpreadsheet size={18} className="mr-2" />
              Processamentos Excel
            </button>
            <button 
              className={`historico-tab ${abaAtiva === 'processamentos' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('processamentos')}
            >
              <Calendar size={18} className="mr-2" />
              Processamentos
            </button>
            <button 
              className={`historico-tab ${abaAtiva === 'envios' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('envios')}
            >
              <MessageSquare size={18} className="mr-2" />
              Envios Individuais
            </button>
          </div>
        </div>

        {/* Conteúdo das Abas */}
        {abaAtiva === 'jobs-excel' && (
          <>
            {loadingJobs ? (
              <div className="text-center p-6">
                <div className="loading"></div>
                <p className="mt-4">Carregando processamentos Excel...</p>
              </div>
            ) : jobsExcel.length === 0 ? (
              <div className="text-center p-6">
                <FileSpreadsheet size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Nenhum processamento Excel encontrado</p>
              </div>
            ) : (
              <div className="historico-table-container">
                <table className="historico-table">
                  <thead>
                    <tr>
                      <th>Arquivo</th>
                      <th>Status</th>
                      <th>Progresso</th>
                      <th>Total</th>
                      <th>Processados</th>
                      <th>Enviados</th>
                      <th>Iniciado</th>
                      <th>Finalizado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobsExcel.map((job) => (
                      <tr key={job.job_id}>
                        <td>
                          <div className="flex items-center gap-2">
                            <FileSpreadsheet size={16} className="text-gray-400" />
                            <span className="font-medium">{job.arquivo_nome || 'N/A'}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`status-badge ${
                            job.status === 'concluido' ? 'status-success' :
                            job.status === 'erro' ? 'status-error' :
                            job.status === 'processando' ? 'status-warning' : 'status-info'
                          }`}>
                            {job.status === 'pendente' && (
                              <>
                                <Clock size={14} className="inline mr-1" />
                                Pendente
                              </>
                            )}
                            {job.status === 'processando' && (
                              <>
                                <Play size={14} className="inline mr-1" />
                                Processando...
                              </>
                            )}
                            {job.status === 'concluido' && (
                              <>
                                <CheckCircle size={14} className="inline mr-1" />
                                Concluído
                              </>
                            )}
                            {job.status === 'erro' && (
                              <>
                                <XCircle size={14} className="inline mr-1" />
                                Erro
                              </>
                            )}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div style={{
                              width: '100px',
                              height: '8px',
                              backgroundColor: '#e5e7eb',
                              borderRadius: '4px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                width: `${job.progresso}%`,
                                height: '100%',
                                backgroundColor: job.status === 'erro' ? '#ef4444' : 
                                               job.status === 'concluido' ? '#10b981' : '#3b82f6',
                                transition: 'width 0.3s ease'
                              }} />
                            </div>
                            <span className="text-sm font-semibold">{job.progresso}%</span>
                          </div>
                        </td>
                        <td className="font-semibold">{job.total || 0}</td>
                        <td className="font-semibold">{job.processados || 0}</td>
                        <td className="font-semibold text-green-600">{job.enviados || 0}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Calendar size={16} className="text-gray-400" />
                            {job.criado_em ? formatarData(job.criado_em) : 'N/A'}
                          </div>
                        </td>
                        <td>
                          {job.finalizado_em ? (
                            <div className="flex items-center gap-2">
                              <Calendar size={16} className="text-gray-400" />
                              {formatarData(job.finalizado_em)}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {abaAtiva === 'processamentos' && (
          <>
            {historicoFiltrado.length === 0 ? (
          <div className="text-center p-6">
            <Calendar size={48} className="text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Nenhum processamento encontrado</p>
          </div>
        ) : (
          <div className="historico-table-container">
            <table className="historico-table">
              <thead>
                <tr>
                  <th>Data/Hora</th>
                  <th>Status</th>
                  <th>Processados</th>
                  <th>Enviados</th>
                  <th>Erro</th>
                </tr>
              </thead>
              <tbody>
                {historicoFiltrado.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-gray-400" />
                        {formatarData(item.data_processamento)}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${item.sucesso ? 'status-success' : 'status-error'}`}>
                        {item.sucesso ? (
                          <>
                            <CheckCircle size={14} className="inline mr-1" />
                            Sucesso
                          </>
                        ) : (
                          <>
                            <XCircle size={14} className="inline mr-1" />
                            Erro
                          </>
                        )}
                      </span>
                    </td>
                    <td className="font-semibold">{item.total_processados || 0}</td>
                    <td className="font-semibold text-green-600">{item.total_enviados || 0}</td>
                    <td>
                      {item.erro ? (
                        <div className="text-red-600 text-sm max-w-xs truncate" title={item.erro}>
                          {item.erro}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          </>
        )}

        {abaAtiva === 'envios' && (
          <>
            {/* Controles de busca e filtro para envios */}
            <div className="historico-search-controls">
              <div className="historico-search-row">
                <div className="historico-search-field">
                  <label>Buscar por nome, celular ou inscrição:</label>
                  <div className="historico-search-input-group">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Digite para buscar..."
                      className="historico-search-input"
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <button
                      onClick={handleSearch}
                      className="historico-search-button"
                    >
                      Buscar
                    </button>
                  </div>
                </div>
                
                <div className="historico-filter-field">
                  <label>Status:</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                    className="historico-filter-select"
                  >
                    <option value="">Todos</option>
                    <option value="sucesso">Sucesso</option>
                    <option value="erro">Erro</option>
                  </select>
                </div>
                
                <button 
                  className="historico-refresh-button"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('');
                    carregarHistoricoEnvios(1, '', '');
                  }}
                >
                  <RefreshCw size={18} />
                  Atualizar
                </button>
              </div>
            </div>

            {loadingEnvios ? (
              <div className="text-center p-6">
                <div className="loading"></div>
                <p className="mt-4">Carregando envios...</p>
              </div>
            ) : historicoEnvios.length === 0 ? (
              <div className="text-center p-6">
                <MessageSquare size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Nenhum envio encontrado</p>
              </div>
            ) : (
              <div className="historico-table-container">
                <table className="historico-table">
                  <thead>
                    <tr>
                      <th>Data/Hora</th>
                      <th>Responsável</th>
                      <th>Celular</th>
                      <th>Inscrição</th>
                      <th>Parcela</th>
                      <th>Valor</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historicoEnvios.map((envio, index) => (
                      <tr key={index}>
                        <td>
                          <div className="flex items-center gap-2">
                            <Calendar size={16} className="text-gray-400" />
                            {formatarData(envio.data_envio)}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <User size={16} className="text-gray-400" />
                            {envio.responsavel || 'N/A'}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Phone size={16} className="text-gray-400" />
                            {envio.celular || 'N/A'}
                          </div>
                        </td>
                        <td className="font-mono text-sm">{envio.inscricao_municipal || 'N/A'}</td>
                        <td className="font-mono text-sm font-semibold">
                          {envio.numero_parcela && envio.total_parcelas 
                            ? `${envio.numero_parcela}/${envio.total_parcelas}` 
                            : 'N/A'}
                        </td>
                        <td className="font-semibold">
                          {envio.valor_parcela ? `R$ ${parseFloat(envio.valor_parcela).toFixed(2)}` : 'N/A'}
                        </td>
                        <td>
                          <span className={`status-badge ${envio.sucesso ? 'success' : 'error'}`}>
                            {envio.sucesso ? (
                              <>
                                <CheckCircle size={14} className="inline mr-1" />
                                Sucesso
                              </>
                            ) : (
                              <>
                                <XCircle size={14} className="inline mr-1" />
                                Erro
                              </>
                            )}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Paginação para envios */}
            {enviosPagination.totalPages > 1 && (
              <div className="historico-pagination-container">
                <div className="flex items-center justify-between">
                  <div className="historico-pagination-info">
                    Mostrando {((enviosPagination.page - 1) * enviosPagination.limit) + 1} a {Math.min(enviosPagination.page * enviosPagination.limit, enviosPagination.total)} de {enviosPagination.total} envios
                  </div>
                  
                  <div className="historico-pagination-controls">
                    <button
                      onClick={() => handlePageChange(enviosPagination.page - 1)}
                      disabled={enviosPagination.page === 1}
                      className="historico-pagination-button"
                    >
                      Anterior
                    </button>
                    
                    {Array.from({ length: Math.min(5, enviosPagination.totalPages) }, (_, i) => {
                      const pageNum = i + 1;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`historico-pagination-button ${enviosPagination.page === pageNum ? 'active' : ''}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handlePageChange(enviosPagination.page + 1)}
                      disabled={enviosPagination.page === enviosPagination.totalPages}
                      className="historico-pagination-button"
                    >
                      Próximo
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="historico-grid">
        <div className="historico-stat-card">
          <div className="historico-stat-icon bg-blue-50">
            <RefreshCw className="text-blue-600" size={24} />
          </div>
          <div className="historico-stat-content">
            <h3 className="historico-stat-title">Total de Execuções</h3>
            <p className="historico-stat-value">{estatisticas.total_execucoes}</p>
          </div>
        </div>

        <div className="historico-stat-card">
          <div className="historico-stat-icon bg-green-50">
            <CheckCircle className="text-green-600" size={24} />
          </div>
          <div className="historico-stat-content">
            <h3 className="historico-stat-title">Execuções com Sucesso</h3>
            <p className="historico-stat-value">{estatisticas.execucoes_sucesso}</p>
          </div>
        </div>

        <div className="historico-stat-card">
          <div className="historico-stat-icon bg-red-50">
            <XCircle className="text-red-600" size={24} />
          </div>
          <div className="historico-stat-content">
            <h3 className="historico-stat-title">Execuções com Erro</h3>
            <p className="historico-stat-value">{estatisticas.execucoes_erro}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Historico;
